import { z } from "zod"

// These limits count UTF-16 code units, matching String.length, HTML maxlength,
// and the existing API/form contracts. Zod 4.5 changed its built-in string bounds
// to count Unicode code points instead.
export function utf16Length(kind: "min" | "max" | "exact", limit: number, error?: string) {
	return <T extends z.ZodType<string>>(schema: T): T => {
		const check = z.check<string>((context) => {
			const length = context.value.length
			const tooSmall = kind !== "max" && length < limit
			const tooBig = kind !== "min" && length > limit
			if (!tooSmall && !tooBig) return
			context.issues.push({
				...(tooSmall ? { code: "too_small", minimum: limit } as const : { code: "too_big", maximum: limit } as const),
				origin: "string",
				inclusive: true,
				input: context.value,
				...(error === undefined ? {} : { message: error }),
				continue: true,
			})
		})
		return schema.check(check).meta({
			...schema.meta(),
			...(kind !== "max" ? { minLength: limit } : {}),
			...(kind !== "min" ? { maxLength: limit } : {}),
		})
	}
}

function isoTimestamp(offset: boolean) {
	// Both precisions are part of the existing contract. Keep a single format
	// check so invalid values still report invalid_format at the original path.
	const minutes = z.regexes.datetime({ offset, precision: -1 })
	const seconds = z.regexes.datetime({ offset })
	return z.stringFormat("datetime", new RegExp(`(?:${minutes.source})|(?:${seconds.source})`))
		.meta({ format: "date-time" })
}

export const IsoTimestampSchema = isoTimestamp(false)
export const OffsetIsoTimestampSchema = isoTimestamp(true)

// Provider URLs use WHATWG URL parsing, including its normalization rules, but
// keep the original string. z.url({ protocol }) now adds stricter slash checks.
export const HttpUrlSchema = z.stringFormat("url", (value) => {
	try {
		const protocol = new URL(value).protocol
		return protocol === "http:" || protocol === "https:"
	} catch {
		return false
	}
}).meta({ format: "uri" })
